import React from 'react';
import Currency from '@app/model/_common/Currency';
import PriceSubmissionBlockOutput from '@app/model/pricing-automation/output/PriceSubmissionBlockOutput';
import Utilities from '@util/Utilities';
import { formatProviderPriceBlock, formatPercentage, isProviderPriceBlock, parseGraphQLDouble } from './PriceSubmissionBlockedItems.utilities';

const PriceSubmissionBlockedItems: React.FunctionComponent<{
	items: PriceSubmissionBlockOutput[],
	currency: Currency,
	onDateSelect: (statDate: string) => void,
}> = ({ items, currency, onDateSelect }) => {
	const groupedItems = React.useMemo(() => {
		const groups = new Map<string, PriceSubmissionBlockOutput[]>();
		items.forEach(item => groups.set(item.statDate, [...(groups.get(item.statDate) ?? []), item]));
		return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right));
	}, [items]);

	if (items.length === 0) return null;

	return <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-900">
		<p className="font-semibold">
			We did not send any prices. {groupedItems.length} blocked {groupedItems.length === 1 ? 'date' : 'dates'} must be corrected:
		</p>
		<div className="mt-3 space-y-3">
			{groupedItems.map(([statDate, blockedItems]) => <div key={statDate}>
				<button type="button"
							className="font-semibold text-red-800 underline underline-offset-2 hover:text-red-600"
							onClick={() => onDateSelect(statDate)}>
					{Utilities.formatDateString(statDate, 'DD MMM', false)}
				</button>
				<ul className="mt-1 space-y-1 pl-4">
					{blockedItems.map(item => <li key={`${item.statDate}-${item.roomId}`} className="list-disc">
						<span className="font-medium">{item.roomName}</span>: {' '}
						{isProviderPriceBlock(item.errorCode) ? formatProviderPriceBlock(item, amount => Utilities.formatAmount(amount, currency)) : <>
						{Utilities.formatAmount(parseGraphQLDouble(item.currentPrice), currency)} &rarr; {' '}
						{Utilities.formatAmount(parseGraphQLDouble(item.submittedPrice), currency)} {' '}
						({formatPercentage(item.differencePercentage)}; limit &plusmn;{parseGraphQLDouble(item.limitPercentage).toFixed(1)}%)
						</>}
					</li>)}
				</ul>
			</div>)}
		</div>
		{items.some(item => isProviderPriceBlock(item.errorCode)) && <p className="mt-3 text-xs text-red-800">
			Refresh the recommendation, or change the provider tolerance in Price Settings, then try again.
		</p>}
		{items.some(item => !isProviderPriceBlock(item.errorCode)) && <p className="mt-3 text-xs text-red-800">
			Correct these prices or update the permitted variance in Price Settings, then try again.
		</p>}
	</div>;
};

export default PriceSubmissionBlockedItems;
